import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {iso8601DateTimeZodValidator, uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {z as zod} from "zod";

export type OttokartCustomerCategoryRequest = {
    id: Uuid;
    createdAt: Iso8601DateTime;
    updatedAt: Iso8601DateTime;
    customerId: Uuid;
    category: string;
};

export const ottokartCustomerCategoryRequestValidator: zod.ZodType<OttokartCustomerCategoryRequest> = zod.object({
    id: uuidZodValidator,
    createdAt: iso8601DateTimeZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    customerId: uuidZodValidator,
    category: zod.string(),
});

export type OttokartCustomerFleetProfile = {
    id: Uuid;
    createdAt: Iso8601DateTime;
    updatedAt: Iso8601DateTime;
    customerId: Uuid;
    machineType: string;
    manufacturer: string;
    modelNo: string;
    machineName: string;
    serialNo: string | null;
    vin: string | null;
    fleetStatus: string | null;
    address: string | null;
    city: string | null;
    state: string | null;
    pinCode: string | null;
    purchaseDate: string | null;
    imageUrl: string | null;
    isNew: boolean;
    approvalStatus: string | null;
};

export const ottokartCustomerFleetProfileValidator: zod.ZodType<OttokartCustomerFleetProfile> = zod.object({
    id: uuidZodValidator,
    createdAt: iso8601DateTimeZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    customerId: uuidZodValidator,
    machineType: zod.string(),
    manufacturer: zod.string(),
    modelNo: zod.string(),
    machineName: zod.string(),
    serialNo: zod.string().nullable(),
    vin: zod.string().nullable(),
    fleetStatus: zod.string().nullable(),
    address: zod.string().nullable(),
    city: zod.string().nullable(),
    state: zod.string().nullable(),
    pinCode: zod.string().nullable(),
    purchaseDate: zod.string().nullable(),
    imageUrl: zod.string().nullable(),
    isNew: zod.boolean(),
    approvalStatus: zod.string().nullable(),
});

export type OttokartCustomerPreferences = {
    id: Uuid;
    createdAt: Iso8601DateTime;
    updatedAt: Iso8601DateTime;
    customerId: Uuid;
    variantIds: Array<Uuid>;
};

export const ottokartCustomerPreferencesValidator: zod.ZodType<OttokartCustomerPreferences> = zod.object({
    id: uuidZodValidator,
    createdAt: iso8601DateTimeZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    customerId: uuidZodValidator,
    variantIds: zod.array(uuidZodValidator),
});

export type OttokartCustomerProductActivityItem = {
    id: Uuid;
    createdAt: Iso8601DateTime;
    customerId: Uuid;
    productId: Uuid;
    variantId: Uuid | null;
    displayName: string;
    sku: string | null;
    slug: string | null;
    imageId: Uuid | null;
    quantity: number | null;
};

export const ottokartCustomerProductActivityItemValidator: zod.ZodType<OttokartCustomerProductActivityItem> = zod.object({
    id: uuidZodValidator,
    createdAt: iso8601DateTimeZodValidator,
    customerId: uuidZodValidator,
    productId: uuidZodValidator,
    variantId: uuidZodValidator.nullable(),
    displayName: zod.string(),
    sku: zod.string().nullable(),
    slug: zod.string().nullable(),
    imageId: uuidZodValidator.nullable(),
    quantity: zod.number().nullable(),
});

export type OttokartCustomerFleetStatusLog = {
    id: Uuid;
    createdAt: Iso8601DateTime;
    updatedAt: Iso8601DateTime;
    fleetId: Uuid;
    status: string;
    images: Array<string>;
    documents: Array<string>;
    comment: string | null;
};

export const ottokartCustomerFleetStatusLogValidator: zod.ZodType<OttokartCustomerFleetStatusLog> = zod.object({
    id: uuidZodValidator,
    createdAt: iso8601DateTimeZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    fleetId: uuidZodValidator,
    status: zod.string(),
    images: zod.array(zod.string()),
    documents: zod.array(zod.string()),
    comment: zod.string().nullable(),
});

/** Admin customers-table enrichment: quotation families + fleet labels + preference tags. */
export type OttokartCustomerTableAggregate = {
    customerId: Uuid;
    quotationCount: number;
    machines: Array<string>;
    categories: Array<string>;
    partTypes: Array<string>;
};

export const ottokartCustomerTableAggregateValidator: zod.ZodType<OttokartCustomerTableAggregate> = zod.object({
    customerId: uuidZodValidator,
    quotationCount: zod.number().int().nonnegative(),
    machines: zod.array(zod.string()),
    categories: zod.array(zod.string()),
    partTypes: zod.array(zod.string()),
});
